import React, { forwardRef, useState } from 'react'
import { observer } from 'mobx-react'
import { Order } from '../../Types/Order'
import DocketItems from './DocketItems'
import dayjs from 'dayjs'
import { Pill } from '~shared/lib/Pill/Pill'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import './Docket.scss'
import { Button, stylesButton } from '~shared/lib/Button/Button'
import clsx from 'clsx'
import { DocketAction } from './DocketAction'
import { Marker } from '@instacart/tds'
import { OrderUtil } from '../../Utils/OrderUtil'

// Custom parse is for different in formats returned from endpoint, breaks for no US dates if not formatted
dayjs.extend(customParseFormat)
dayjs.extend(isSameOrBefore)

interface DocketProps {
  order: Order
  config: any
  printingEnabled?: boolean
  printerLoading: string
  buttonLabel: string
  position: number
  print: (event: React.MouseEvent<HTMLButtonElement>) => void
  action: (order: Order) => Promise<void>
  confirmWeightAction: (event: React.MouseEvent<HTMLButtonElement>) => void
  fetchOrders?: () => void
  onEditOrder?: () => void
}

type TimeClass = 'warning' | 'error' | 'success' | 'inverse'

export const Docket = observer(
  forwardRef((props: DocketProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const {
      order,
      config,
      printingEnabled,
      printerLoading,
      buttonLabel,
      position,
      print,
      action,
      confirmWeightAction,
      fetchOrders,
      onEditOrder,
    } = props

    const completed = order.status == 'Complete'
    const [isDisabled, setIsDisabled] = useState(completed)
    const containsWeightedItem = order?.items?.some(
      item => item?.weightType != 'Fixed' && !item?.pack
    )
    const isNotToday = !dayjs(order.timeFrom).isSame(dayjs(), 'day')
    const orderDate = dayjs(order.timeFrom).format('DD MMM')

    const calcTimeFromNow = (date: string): TimeClass => {
      const now = dayjs(new Date())
      const timeDiff = dayjs(date).diff(now, 'minute')
      const TIME_OVERDUE_WINDOW = 10
      const TIME_WARNING_WINDOW = 15
      let timeClass: TimeClass = 'inverse'

      if (timeDiff >= TIME_OVERDUE_WINDOW && timeDiff <= TIME_WARNING_WINDOW) {
        timeClass = 'warning'
      } else if (timeDiff < TIME_OVERDUE_WINDOW) {
        timeClass = 'error'
      } else {
        timeClass = 'success'
      }

      return timeClass
    }

    const callAction = async () => {
      setIsDisabled(true)

      try {
        await action(order)
      } catch (error) {
        console.error('Unexpected error in Docket callAction:', error)
      } finally {
        setIsDisabled(false)
      }
    }

    return (
      <>
        <div
          className={clsx(
            'docket',
            order.status,
            `docket-${order.orderNo}`,
            `docket-position-${position}`,
            { 'weighted-order': containsWeightedItem }
          )}
          tabIndex={0}
          data-orderno={order.orderNo}
          ref={ref}
        >
          <div className="docket-inner">
            <div className="docket-header">
              <div className="docket-header-order">
                <h4>
                  Order {order.orderNo}{' '}
                  {order.instacart && <span className="icon marketplaceordericon"></span>}
                </h4>
                <p>{order.deliveryContactName}</p>
              </div>

              {order.timeFrom && (
                <Pill color={calcTimeFromNow(order.timeFrom)}>
                  {isNotToday ? orderDate + ', ' : ''}
                  {dayjs(order.timeFrom).format('hh:mm a')}
                </Pill>
              )}

              {printingEnabled && (
                <div className="docket-print">
                  {printerLoading && printerLoading == order.orderNo ? (
                    <div className="fs-loading-small"></div>
                  ) : (
                    <Button
                      onClick={print}
                      className={clsx(
                        'print-button',
                        stylesButton.buttonSecondary,
                        stylesButton.buttonSmall
                      )}
                    >
                      <div className="icon printicon"></div>
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="docket-markers">
              {OrderUtil.isPaid(order) ? (
                <Marker text="Paid" variant={'success'} />
              ) : (
                <Marker text="Unpaid" variant={'detrimental'} />
              )}
            </div>
            <DocketItems items={order.items} note={order.productionNote ?? ''} />

            <DocketAction
              order={order}
              config={config}
              isDisabled={isDisabled}
              buttonLabel={buttonLabel}
              callAction={callAction}
              confirmWeightAction={confirmWeightAction}
              fetchOrders={fetchOrders}
              onEditOrder={onEditOrder}
            />
          </div>
        </div>
      </>
    )
  })
)
